import { useState, useEffect } from 'react'
import { getProducts, getProfiles, updateProfile } from '../../services/api'
import ProductSelector from '../ProductSelector'

// EditPanel - handles profile product editing with drag/drop reordering
export default function EditPanel({ profile, onSave, onCancel, onSaveEdit, onItemsPerRowChange, onItemSizeChange, initialItemsPerRow = 0, isOverlay = false, onLiveChange, onRefresh }) {
  const [allProducts, setAllProducts] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [sortOrder, setSortOrder] = useState([])
  const [itemsPerRow, setItemsPerRow] = useState(initialItemsPerRow)
  const [itemSize, setItemSize] = useState(() => {
    const saved = localStorage.getItem(`itemSize_${profile?.id || 'default'}`)
    return saved ? parseInt(saved) : 1 // 0=Klein, 1=Mittel, 2=Groß
  })

  // Load settings from localStorage after mount
  useEffect(() => {
    const savedRows = localStorage.getItem(`itemsPerRow_${profile?.id || 'default'}`)
    if (savedRows) setItemsPerRow(parseInt(savedRows))
    const savedSize = localStorage.getItem(`itemSize_${profile?.id || 'default'}`)
    if (savedSize) setItemSize(parseInt(savedSize))
  }, [profile?.id])

  // Guard: don't render if no profile
  if (!profile) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Profil wird geladen...</p>
      </div>
    )
  }

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!profile?.id) return
    
    setIsLoading(true)
    const loadProducts = async () => {
      try {
        const all = await getProducts()
        const profiles = await getProfiles()
        const currentProfile = profiles.find(p => p.id === profile.id)
        
        setAllProducts(all)
        
        // Parse produkte and sortierung directly from profile
        const produkteStr = currentProfile?.produkte || ''
        const sortierungStr = currentProfile?.sortierung || ''
        
        const profileIds = produkteStr ? produkteStr.split(',').filter(x => x.trim()).map(x => parseInt(x)) : []
        const sortIds = sortierungStr ? sortierungStr.split(',').filter(x => x.trim()).map(x => parseInt(x)) : []
        
        setSelectedIds(profileIds)
        setSortOrder(sortIds)
      } finally {
        setIsLoading(false)
      }
    }
    loadProducts()
  }, [profile])

  const handleSave = async () => {
    if (!profile?.id) return
    
    const includedIds = selectedIds.join(',')
    
    await updateProfile({
      id: profile.id,
      name: profile.name,
      color: profile.color,
      produkte: includedIds,
      sortierung: sortOrder.join(',')
    })
    
    localStorage.setItem(`itemsPerRow_${profile.id}`, itemsPerRow.toString())
    
    // Call both internal onSave and external onSaveEdit if provided
    onSave()
    if (onSaveEdit) onSaveEdit()
  }

  const handleChange = async (newSelectedIds, newSortOrder) => {
    setSelectedIds(newSelectedIds)
    setSortOrder(newSortOrder)
    
    // Auto-save to API
    if (profile?.id) {
      try {
        await updateProfile({
          id: profile.id,
          name: profile.name,
          color: profile.color,
          produkte: newSelectedIds.join(','),
          sortierung: newSortOrder.join(',')
        })
        // Trigger product refresh in parent (without closing the panel)
        if (onRefresh) onRefresh()
      } catch (err) {
        console.error('Failed to save:', err)
      }
    }
  }

  // Listen for save trigger from header
  useEffect(() => {
    const handleSaveTrigger = () => handleSave()
    window.addEventListener('editPanelSave', handleSaveTrigger)
    return () => window.removeEventListener('editPanelSave', handleSaveTrigger)
  }, [handleSave])

  const styles = {
    editModal: {
      position: isOverlay ? 'fixed' : 'relative',
      top: isOverlay ? 0 : 'auto',
      left: isOverlay ? 0 : 'auto',
      right: isOverlay ? 0 : 'auto',
      bottom: isOverlay ? 0 : 'auto',
      width: '100%',
      height: '100%',
      background: '#eee',
      zIndex: isOverlay ? 300 : 'auto',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
    },
    editSection: {
      flex: 1,
      overflow: 'auto',
      padding: '80px 20px 20px',
    },
  }

  return (
    <div style={styles.editModal}>
      <div style={styles.editSection}>
        {/* Items per row setting */}
        <div style={{ marginBottom: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '10px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>📱 Produkte pro Reihe: {itemsPerRow === 0 ? 'Auto' : itemsPerRow}</div>
          <select
            value={itemsPerRow}
            onChange={(e) => {
              const value = parseInt(e.target.value)
              setItemsPerRow(value)
              localStorage.setItem(`itemsPerRow_${profile.id}`, value.toString())
              if (onItemsPerRowChange) onItemsPerRowChange(value)
            }}
            style={{ width: '100%', padding: '8px', fontSize: '16px', borderRadius: '5px' }}
          >
            <option value="0">Auto (dynamisch)</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10</option>
          </select>
        </div>

        {/* Item size setting */}
        <div style={{ marginBottom: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '10px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>📐 Produktgröße: {['Klein', 'Mittel', 'Groß'][itemSize]}</div>
          <select
            value={itemSize}
            onChange={(e) => {
              const value = parseInt(e.target.value)
              setItemSize(value)
              localStorage.setItem(`itemSize_${profile.id}`, value.toString())
              if (onItemSizeChange) onItemSizeChange(value)
            }}
            style={{ width: '100%', padding: '8px', fontSize: '16px', borderRadius: '5px' }}
          >
            <option value="0">Klein</option>
            <option value="1">Mittel</option>
            <option value="2">Groß</option>
          </select>
        </div>

        {/* Product Selector */}
        <ProductSelector
          allProducts={allProducts}
          selectedIds={selectedIds}
          sortOrder={sortOrder}
          onChange={handleChange}
        />

        {/* Done Button - closes the edit panel */}
        <button 
          onClick={() => {
            if (onCancel) onCancel()
          }}
          style={{
            width: '100%',
            padding: '15px',
            background: '#4CAF50',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginTop: '20px',
            marginBottom: '40px',
          }}
        >
          Fertig
        </button>
      </div>
    </div>
  )
}